import { useQueryClient } from '@tanstack/react-query'
import { base } from 'viem/chains'

import { useAutomations } from '~/app/automations/useAutomations'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { SmartAccountRole } from '~/types'
import { Automation, AutomationPolicy } from '~/types/automations'
import { Address, EvmAddress } from '~/utils/address'
import { DEFAULT_SLIPPAGE_BPS } from '~/utils/swap'
import { getAutoEarnVault } from '~/utils/vault'

const isEarnDepositAutomation = (
  automation: Automation,
  treasuryAddress: EvmAddress.Type,
) => {
  const [output, ...rest] = automation.policy.outputs
  return (
    automation.isActive &&
    rest.length === 0 &&
    output?.percent === 100 &&
    output.targetChainId === base.id &&
    Address.equal(output.targetTokenAddress, getAutoEarnVault()) &&
    Address.equal(output.destinationAddress, treasuryAddress)
  )
}

// Finds the org's Earn deposit automation (100% into the Earn vault on Base,
// landing in the Treasury) in the shared automations list, creating one at a
// $1 threshold only once the list has loaded without a match. An existing
// automation is used as configured; its threshold is the owner's choice.
export const useEarnDepositAutomation = ({
  enabled,
}: {
  enabled: boolean
}): {
  automation: Automation | undefined
  isError: boolean
  retry: () => void
} => {
  const orgId = useActiveOrgId()
  const queryClient = useQueryClient()
  const { smartAccounts } = useSmartAccounts()
  const { serverPostRequest } = useServerRequests()
  const treasury = smartAccounts.find(
    (account) => account.role === SmartAccountRole.TREASURY,
  )
  const treasuryAddress = treasury && EvmAddress.format(treasury.address)

  const {
    automations,
    isSuccess: hasLoadedAutomations,
    isError: hasListError,
    refetch: refetchList,
  } = useAutomations({ enabled: enabled && !!treasuryAddress })
  const existing =
    treasuryAddress &&
    automations.find((automation) =>
      isEarnDepositAutomation(automation, treasuryAddress),
    )

  // Keyed outside AUTOMATIONS so invalidating the list never re-runs the
  // create.
  const {
    data: created,
    isError: hasCreateError,
    refetch: refetchCreate,
  } = useQueryWrapper<Automation>({
    queryKey: [QUERY_KEYS.EARN_DEPOSIT_AUTOMATION, orgId],
    queryFn: async () => {
      if (!orgId || !treasuryAddress) throw new Error('Treasury required')

      const policy: AutomationPolicy = {
        version: 1,
        minimumThresholdUsd: 1,
        slippage: DEFAULT_SLIPPAGE_BPS,
        passThroughPercent: 0,
        passThroughDestinationAddress: treasuryAddress,
        outputs: [
          {
            percent: 100,
            targetChainId: base.id,
            targetTokenAddress: getAutoEarnVault(),
            destinationAddress: treasuryAddress,
          },
        ],
      }
      const response = await serverPostRequest<{ automation: Automation }>({
        path: '/api/org/automations',
        errorMessage: 'Failed to create deposit address',
        errorToastId: 'earn-deposit-automation',
        params: { orgId, name: 'Earn deposits', policy },
      })
      if (!response) throw new Error('Failed to create deposit address')
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.AUTOMATIONS, orgId],
      })
      return response.data.automation
    },
    enabled:
      enabled &&
      !!orgId &&
      !!treasuryAddress &&
      hasLoadedAutomations &&
      !existing,
  })

  return {
    automation: existing || created,
    isError: hasListError || hasCreateError,
    retry: () => {
      void refetchList()
      if (hasCreateError) void refetchCreate()
    },
  }
}
